/**
 * Legacy auth compatibility hook
 *
 * Provides twitterUser-shaped object for backwards compatibility.
 * Checks new auth system first, then falls back to localStorage.
 */

import { useMemo } from 'react';
import { useAuthContext } from '../contexts/AuthContext';

/**
 * Legacy TwitterUser shape (used by existing components)
 */
export interface LegacyTwitterUser {
  screen_name: string;
  user_id: string;
  name?: string;
  profile_image_url_https?: string;
}

/**
 * Return type for useLegacyAuth hook
 */
export interface UseLegacyAuthReturn {
  /** User in legacy twitterUser format (null if not authenticated) */
  twitterUser: LegacyTwitterUser | null;
  /** Whether user is authenticated (via either auth system) */
  isAuthenticated: boolean;
  /** Display name (screen_name for Twitter, displayName for wallet) */
  displayName: string | null;
  /** User ID from new auth system (null if using legacy or not authenticated) */
  userId: string | null;
  /** Auth type: 'twitter', 'wallet', 'legacy', or null */
  authType: 'twitter' | 'wallet' | 'legacy' | null;
}

/**
 * Hook that provides backwards-compatible twitterUser for existing components.
 *
 * Priority:
 * 1. New auth system (wallet or Twitter OAuth 2.0)
 * 2. Legacy localStorage twitterUser (OAuth 1.0)
 */
export function useLegacyAuth(): UseLegacyAuthReturn {
  const auth = useAuthContext();

  return useMemo(() => {
    // Priority 1: New auth system
    if (auth.isAuthenticated && auth.user) {
      const user = auth.user;

      // Convert new auth user to legacy twitterUser format
      const twitterUser: LegacyTwitterUser = {
        screen_name: user.authType === 'twitter'
          ? (user.twitterHandle || user.displayName)
          : user.displayName,
        user_id: user.id,
        name: user.displayName,
        profile_image_url_https: user.profileImageUrl || undefined,
      };

      return {
        twitterUser,
        isAuthenticated: true,
        displayName: user.displayName,
        userId: user.id,
        authType: user.authType,
      };
    }

    // Priority 2: Legacy localStorage twitterUser
    try {
      const storedUser = localStorage.getItem('twitterUser');
      if (storedUser) {
        const legacyUser = JSON.parse(storedUser) as LegacyTwitterUser;
        if (legacyUser.screen_name && legacyUser.user_id) {
          return {
            twitterUser: legacyUser,
            isAuthenticated: true,
            displayName: legacyUser.screen_name,
            userId: null, // Legacy users don't have new system user_id
            authType: 'legacy' as const,
          };
        }
      }
    } catch {
      // Invalid stored data, ignore
    }

    // Not authenticated
    return {
      twitterUser: null,
      isAuthenticated: false,
      displayName: null,
      userId: null,
      authType: null,
    };
  }, [auth.isAuthenticated, auth.user]);
}

export default useLegacyAuth;
